import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db, getDoc, getDocs } from '@/lib/firebase';
import { Board, Pixel } from '@/types';
import { validateBoardDimensions } from '@/lib/config';
import { replayModifications } from './modification.service';
import { logError } from './analytics.service';
import { logger } from '@/lib/logger';
import { getUserByUsername, removeBoardFromUser } from './user.service';

export const createBoard = async (
  ownerId: string,
  ownerUsername: string,
  name: string,
  width: number,
  height: number,
  isPublic: boolean = true,
  isSpecialEvent: boolean = false,
  specialEventPixels?: number,
  customPalette?: string[]
): Promise<Board> => {
  try {
    // Validate dimensions
    if (!validateBoardDimensions(width, height)) {
      throw new Error(
        'Board dimensions exceed maximum allowed pixels (400,000)'
      );
    }

    // Normalize board name to lowercase
    const normalizedName = name.trim().toLowerCase();

    const boardRef = doc(collection(db, 'boards'));
    const board: Board = {
      id: boardRef.id,
      name: normalizedName,
      ownerId,
      ownerUsername,
      width,
      height,
      maxPixels: width * height,
      createdAt: Timestamp.now(),
      isPublic,
      isSpecialEvent,
      ...(specialEventPixels !== undefined && { specialEventPixels }),
      ...(customPalette !== undefined && { customPalette }),
    };

    await setDoc(boardRef, board);

    return board;
  } catch (error) {
    logger('board').error('Error creating board:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logError('Board Creation Error', errorMessage, 'board.service', {
      owner_id: ownerId,
      board_name: name,
      width,
      height,
    });
    throw error;
  }
};

export const getBoard = async (boardId: string): Promise<Board | null> => {
  try {
    const boardDoc = await getDoc(doc(db, 'boards', boardId));
    if (!boardDoc.exists()) {
      return null;
    }
    return boardDoc.data() as Board;
  } catch (error) {
    logger('board').error('Error getting board:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logError('Get Board Error', errorMessage, 'board.service', { board_id: boardId });
    throw error;
  }
};

export const getBoardByName = async (name: string): Promise<Board | null> => {
  try {
    // Normalize board name to lowercase
    const normalizedName = name.trim().toLowerCase();
    
    const boardsRef = collection(db, 'boards');
    // Query for public boards first (this works with Firestore rules)
    const q = query(
      boardsRef,
      where('name', '==', normalizedName),
      where('isPublic', '==', true)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].data() as Board;
    }

    // If not found in public boards, return null
    // Private boards should be accessed by ID, not by name
    return null;
  } catch (error) {
    logger('board').error('Error getting board by name:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logError('Get Board By Name Error', errorMessage, 'board.service', { board_name: name });
    throw error;
  }
};

/**
 * Check if a board with the given name already exists (both public and private)
 * @param name - The board name to check
 * @param userId - Optional user ID to also check their private boards
 * @returns true if a board with this name exists, false otherwise
 */
export const checkBoardNameExists = async (
  name: string,
  userId?: string
): Promise<boolean> => {
  try {
    // Normalize board name to lowercase
    const normalizedName = name.trim().toLowerCase();
    
    const boardsRef = collection(db, 'boards');
    
    // Check public boards (anyone can see public boards)
    const publicQuery = query(
      boardsRef,
      where('name', '==', normalizedName),
      where('isPublic', '==', true)
    );
    const publicSnapshot = await getDocs(publicQuery);
    
    if (!publicSnapshot.empty) {
      return true;
    }
    
    // If userId is provided, also check user's own private boards
    // Note: Firestore security rules will only allow access to user's own private boards
    if (userId) {
      const privateQuery = query(
        boardsRef,
        where('name', '==', normalizedName),
        where('isPublic', '==', false),
        where('ownerId', '==', userId)
      );
      const privateSnapshot = await getDocs(privateQuery);
      
      if (!privateSnapshot.empty) {
        return true;
      }
    }
    
    return false;
  } catch (error) {
    logger('board').error('Error checking board name existence:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logError('Check Board Name Exists Error', errorMessage, 'board.service', { board_name: name });
    throw error;
  }
};


export const getAllBoards = async (userId?: string): Promise<Board[]> => {
  try {
    const boardsRef = collection(db, 'boards');
    
    // Query only public boards (this works with Firestore rules)
    const q = query(boardsRef, where('isPublic', '==', true));
    const querySnapshot = await getDocs(q);

    let boards = querySnapshot.docs.map((doc) => doc.data() as Board);
    
    // If user is authenticated, also fetch their private boards
    if (userId) {
      const privateBoardsQuery = query(
        boardsRef,
        where('ownerId', '==', userId),
        where('isPublic', '==', false)
      );
      const privateSnapshot = await getDocs(privateBoardsQuery);
      const privateBoards = privateSnapshot.docs.map((doc) => doc.data() as Board);
      boards = [...boards, ...privateBoards];
    }

    return boards;
  } catch (error) {
    logger('board').error('Error getting all boards:', error);
    throw error;
  }
};

/**
 * Get board pixels by replaying all dense modifications
 * This is the single source of truth - all board state comes from modifications
 */
export const getBoardPixels = async (
  boardId: string
): Promise<(Pixel | null)[][]> => {
  try {
    const board = await getBoard(boardId);
    if (!board) {
      throw new Error('Board not found');
    }

    // Replay all modifications to get current state
    return await replayModifications(boardId, board.width, board.height);
  } catch (error) {
    logger('board').error('Error getting board pixels:', error);
    throw error;
  }
};

/**
 * Check if a username is a collaborator on a board
 */
export const isBoardCollaborator = (board: Board, username: string): boolean => {
  return board.collaborators?.includes(username) ?? false;
};

/**
 * Check if a user has access to a board (owner or collaborator)
 */
export const hasBoardAccess = (
  board: Board,
  userId: string,
  username: string
): boolean => {
  // Public boards are accessible to everyone
  if (board.isPublic) {
    return true;
  }
  // Private boards: owner or collaborator
  return board.ownerId === userId || isBoardCollaborator(board, username);
};

/**
 * Update board collaborators list
 * @param boardId - The board ID
 * @param collaborators - Array of usernames to set as collaborators
 * @param ownerId - The board owner's user ID (for validation)
 * @returns Updated board
 */
export const updateBoardCollaborators = async (
  boardId: string,
  collaborators: string[],
  ownerId: string
): Promise<Board> => {
  try {
    const board = await getBoard(boardId);
    if (!board) {
      throw new Error('Board not found');
    }

    // Verify caller is the owner
    if (board.ownerId !== ownerId) {
      throw new Error('Only the board owner can update collaborators');
    }

    // Validate max collaborators (20)
    if (collaborators.length > 20) {
      throw new Error('Maximum 20 collaborators allowed');
    }

    // Validate all usernames exist and are not the owner
    for (const username of collaborators) {
      const user = await getUserByUsername(username);
      if (!user) {
        throw new Error(`User "${username}" not found`);
      }
      if (user.uid === ownerId) {
        throw new Error('Board owner cannot be added as collaborator');
      }
    }

    // Remove duplicates
    const uniqueCollaborators = [...new Set(collaborators)];

    // Update board
    const boardRef = doc(db, 'boards', boardId);
    await updateDoc(boardRef, {
      collaborators: uniqueCollaborators,
    });

    // Return updated board
    const updatedBoard = await getBoard(boardId);
    if (!updatedBoard) {
      throw new Error('Failed to fetch updated board');
    }

    return updatedBoard;
  } catch (error) {
    logger('board').error('Error updating board collaborators:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logError('Update Board Collaborators Error', errorMessage, 'board.service', {
      board_id: boardId,
    });
    throw error;
  }
};

/**
 * Add a collaborator to a board
 */
export const addBoardCollaborator = async (
  boardId: string,
  username: string,
  ownerId: string
): Promise<Board> => {
  try {
    const board = await getBoard(boardId);
    if (!board) {
      throw new Error('Board not found');
    }

    const currentCollaborators = board.collaborators || [];
    
    // Check if already a collaborator
    if (currentCollaborators.includes(username)) {
      return board;
    }

    // Check max collaborators
    if (currentCollaborators.length >= 20) {
      throw new Error('Maximum 20 collaborators allowed');
    }

    // Validate username exists and is not owner
    const user = await getUserByUsername(username);
    if (!user) {
      throw new Error(`User "${username}" not found`);
    }
    if (user.uid === ownerId) {
      throw new Error('Board owner cannot be added as collaborator');
    }

    // Add collaborator
    const updatedCollaborators = [...currentCollaborators, username];
    return await updateBoardCollaborators(boardId, updatedCollaborators, ownerId);
  } catch (error) {
    logger('board').error('Error adding board collaborator:', error);
    throw error;
  }
};

/**
 * Remove a collaborator from a board
 */
export const removeBoardCollaborator = async (
  boardId: string,
  username: string,
  ownerId: string
): Promise<Board> => {
  try {
    const board = await getBoard(boardId);
    if (!board) {
      throw new Error('Board not found');
    }

    const currentCollaborators = board.collaborators || [];
    
    // Remove collaborator
    const updatedCollaborators = currentCollaborators.filter(
      (collab) => collab !== username
    );

    return await updateBoardCollaborators(boardId, updatedCollaborators, ownerId);
  } catch (error) {
    logger('board').error('Error removing board collaborator:', error);
    throw error;
  }
};

/**
 * Update board properties (palette, isPublic)
 * @param boardId - The board ID
 * @param updates - Partial board updates (customPalette, isPublic)
 * @param ownerId - The board owner's user ID (for validation)
 * @returns Updated board
 */
export const updateBoard = async (
  boardId: string,
  updates: {
    customPalette?: string[];
    isPublic?: boolean;
  },
  ownerId: string
): Promise<Board> => {
  try {
    const board = await getBoard(boardId);
    if (!board) {
      throw new Error('Board not found');
    }

    // Verify caller is the owner
    if (board.ownerId !== ownerId) {
      throw new Error('Only the board owner can update board settings');
    }

    // Validate palette if provided
    if (updates.customPalette !== undefined) {
      if (
        !Array.isArray(updates.customPalette) ||
        updates.customPalette.length !== 8
      ) {
        throw new Error('Palette must contain exactly 8 colors');
      }
      if (
        !updates.customPalette.every((c) => /^#[0-9A-F]{6}$/i.test(c))
      ) {
        throw new Error('All palette colors must be valid hex colors');
      }
    }

    // Update board
    const boardRef = doc(db, 'boards', boardId);
    const updateData: { customPalette?: string[]; isPublic?: boolean } = {};
    if (updates.customPalette !== undefined) {
      updateData.customPalette = updates.customPalette;
    }
    if (updates.isPublic !== undefined) {
      updateData.isPublic = updates.isPublic;
    }

    await updateDoc(boardRef, updateData);

    // Return updated board
    const updatedBoard = await getBoard(boardId);
    if (!updatedBoard) {
      throw new Error('Failed to fetch updated board');
    }

    return updatedBoard;
  } catch (error) {
    logger('board').error('Error updating board:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logError('Update Board Error', errorMessage, 'board.service', {
      board_id: boardId,
    });
    throw error;
  }
};

/**
 * Delete a board
 * @param boardId - The board ID
 * @param ownerId - The board owner's user ID (for validation)
 */
export const deleteBoard = async (
  boardId: string,
  ownerId: string
): Promise<void> => {
  try {
    const board = await getBoard(boardId);
    if (!board) {
      throw new Error('Board not found');
    }

    // Verify caller is the owner
    if (board.ownerId !== ownerId) {
      throw new Error('Only the board owner can delete the board');
    }

    // Delete board document
    const boardRef = doc(db, 'boards', boardId);
    await deleteDoc(boardRef);

    // Remove board from owner's boards array
    await removeBoardFromUser(ownerId, boardId);
  } catch (error) {
    logger('board').error('Error deleting board:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logError('Delete Board Error', errorMessage, 'board.service', {
      board_id: boardId,
    });
    throw error;
  }
};

